/**
 * Production-ready reimagined Loan Detail page.
 *
 * Renders inside the `account-activity.loans.detail` route when the
 * `reimaginedAccountActivity` feature flag is enabled.
 *
 * Strips out: demo mode, DataIndicator badges, links to reimagined-only
 * routes (finance history, optimizer). Quick actions point to existing
 * production routes.
 */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import PageHero from 'tio-ui/components/page-hero';
import StatCard from 'tio-ui/components/stat-card';
import SectionHeader from 'tio-ui/components/section-header';
import NavCard from 'tio-ui/components/nav-card';
import ProgressBar from 'tio-ui/components/progress-bar';
import {
  PhBank,
  PhArrowLeft,
  PhCurrencyDollar,
  PhPercent,
  PhTrendDown,
  PhCheckCircle,
  PhCreditCard,
  PhSparkle,
  PhHandshake,
  PhStar,
  PhCloudArrowUp,
  PhTarget,
} from 'ember-phosphor-icons';
import type { EmployerContributions } from 'tio-employee/services/career-pathways-api';
import type SessionContextService from 'tio-employee/services/session-context';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { LoanExtension as LoanModel } from 'tio-data/data/resources/loan.ext';

export interface ProductionLoanDetailModel {
  account: AccountModel | undefined;
  person: PersonModel;
  accountId: string;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
}

interface DetailSignature {
  Args: {
    model: ProductionLoanDetailModel;
  };
}

export default class ProductionLoanDetail extends Component<DetailSignature> {
  @service declare sessionContext: SessionContextService;

  get account() {
    return this.args.model.account;
  }

  get activeLoans(): LoanModel[] {
    return this.account?.activeLoans || [];
  }

  get servicerName() {
    return this.account?.institutionName || 'Loan Servicer';
  }

  get totalBalance() {
    return this.activeLoans.reduce((sum, loan) => sum + (loan.currentBalance || 0), 0);
  }

  get totalOriginalBalance() {
    return this.activeLoans.reduce((sum, loan) => sum + (loan.originalAmount || 0), 0);
  }

  get progressPercent() {
    if (!this.totalOriginalBalance) return 0;
    const rawPaid = this.totalOriginalBalance - this.totalBalance;
    const paid = Math.max(0, Math.min(rawPaid, this.totalOriginalBalance));
    const percent = (paid / this.totalOriginalBalance) * 100;
    return Math.round(Math.max(0, Math.min(percent, 100)));
  }

  get weightedInterestRate() {
    return Number(this.account?.loanInterestAverage || 0);
  }

  get totalOriginalBalanceSubtext() {
    return `${formatCurrency(this.totalOriginalBalance)} original`;
  }

  get weightedInterestRateValue() {
    return `${formatRate(this.weightedInterestRate)}%`;
  }

  get paidOffValue() {
    return `${this.progressPercent}%`;
  }

  get paidOffSubtext() {
    return `${formatCurrency(sub(this.totalOriginalBalance, this.totalBalance))} paid down`;
  }

  get monthlyPayment() {
    return this.account?.loanMinMonthlyPayment || this.account?.providerRegularMonthlyPayment || 0;
  }

  get isReceivingContributions() {
    return this.account?.id === this.args.model.person.accountReceivingContributionsId;
  }

  get loanCount() {
    return this.activeLoans.length;
  }

  get heroSubtitle() {
    if (this.loanCount === 0) return 'No active loans found for this account.';
    return `${this.loanCount} active loan${this.loanCount !== 1 ? 's' : ''} with ${this.servicerName}. ${this.isReceivingContributions ? 'This account is receiving employer contributions.' : ''}`;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back Navigation }}
      <div class="mb-2">
        <LinkTo
          @route="authenticated.account-activity.accounts"
          class="tio-inline-row gap-1.5 tio-text-sm-medium tio-fg-muted hover:tio-fg-secondary transition-colors"
        >
          <PhArrowLeft @size={{16}} />
          Back to Loan Summary
        </LinkTo>
      </div>

      {{! Hero }}
      <PageHero
        @icon={{PhBank}}
        @title={{this.servicerName}}
        @subtitle={{this.heroSubtitle}}
        @gradient="brand"
        role="banner"
      >
        <:stat>
          <div class="tio-text-4xl-bold">{{formatCurrency this.totalBalance}}</div>
          <Text class="tio-loan-detail-hero-caption">Current Balance</Text>
        </:stat>
      </PageHero>

      {{! Status Badges }}
      <div class="tio-row mt-4 flex-wrap">
        {{#if this.isReceivingContributions}}
          <Chip @intent="success">
            <PhHandshake @size={{14}} />
            Receiving Contributions
          </Chip>
        {{/if}}
        <Chip @intent="default">
          {{this.loanCount}}
          Loan{{if (isGreater this.loanCount 1) "s"}}
        </Chip>
        {{#if this.account}}
          <Chip @intent="default">
            {{accountType this.activeLoans}}
          </Chip>
        {{/if}}
      </div>

      {{! Account Summary Stats }}
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <StatCard
          @icon={{PhCurrencyDollar}}
          @label="Total Balance"
          @value={{formatCurrency this.totalBalance}}
          @subtext={{this.totalOriginalBalanceSubtext}}
          @color="indigo"
        />
        <StatCard
          @icon={{PhPercent}}
          @label="Avg. Interest Rate"
          @value={{this.weightedInterestRateValue}}
          @subtext="Weighted average"
          @color="blue"
        />
        <StatCard
          @icon={{PhCreditCard}}
          @label="Monthly Payment"
          @value={{formatCurrency this.monthlyPayment}}
          @subtext="Minimum required"
          @color="violet"
        />
        <StatCard
          @icon={{PhTrendDown}}
          @label="Paid Off"
          @value={{this.paidOffValue}}
          @subtext={{this.paidOffSubtext}}
          @color="emerald"
        >
          <:extra>
            <div class="tio-row-extra-tight tio-fg-success text-sm">
              <PhCheckCircle @size={{14}} />
              <span>On track</span>
            </div>
          </:extra>
        </StatCard>
      </div>

      {{! Overall Progress Bar }}
      <div class="mt-6 tio-bg-surface rounded-xl p-6 tio-hairline-card">
        <div class="tio-row-between mb-3">
          <Heading @level={{3}}>
            Payoff Progress
          </Heading>
          <Text @size="sm" class="tio-fg-muted">{{this.progressPercent}}% complete</Text>
        </div>
        <ProgressBar @value={{this.progressPercent}} @color="indigo" @size="lg" />
        <div class="tio-flex-between mt-2">
          <Text @size="xs" class="tio-fg-disabled">{{formatCurrency this.totalOriginalBalance}}
            original</Text>
          <Text @size="xs" class="tio-fg-disabled">{{formatCurrency this.totalBalance}}
            remaining</Text>
        </div>
      </div>

      {{! Individual Loans List }}
      <div class="mt-6">
        <SectionHeader
          @icon={{PhStar}}
          @title="Individual Loans"
          @iconClass="tio-fg-info"
          @iconSize={{24}}
        />
        <div class="space-y-3">
          {{#each this.activeLoans as |loan index|}}
            <div class="tio-bg-surface rounded-xl p-5 tio-hairline-card">
              <div class="tio-row-start-between gap-4">
                <div class="tio-row">
                  <div
                    class="p-2 rounded-lg
                      {{if (isEven index) 'tio-bg-info-soft' 'tio-bg-info-soft'}}"
                  >
                    <PhCurrencyDollar
                      @size={{20}}
                      class="{{if (isEven index) 'tio-fg-info' 'tio-fg-info'}}"
                    />
                  </div>
                  <div>
                    <Text @weight="medium">
                      {{loanTypeName loan}}
                    </Text>
                    {{#if loan.number}}
                      <Text @size="sm" class="tio-fg-muted">····{{lastFour loan.number}}</Text>
                    {{/if}}
                  </div>
                </div>
                <div class="text-right">
                  <Text @size="lg" @weight="semibold">
                    {{formatCurrency loan.currentBalance}}
                  </Text>
                  <Text @size="sm" class="tio-fg-muted">
                    {{formatRate (loanInterestRate loan)}}% APR
                  </Text>
                </div>
              </div>

              {{! Loan detail metrics }}
              <div class="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div class="tio-surface-sunken p-3">
                  <Text @size="xs" class="tio-fg-muted mb-0.5">Original</Text>
                  <Text @size="sm" @weight="medium">
                    {{formatCurrency loan.originalAmount}}
                  </Text>
                </div>
                <div class="tio-surface-sunken p-3">
                  <Text @size="xs" class="tio-fg-muted mb-0.5">Principal</Text>
                  <Text @size="sm" @weight="medium">
                    {{formatCurrency (principalBalance loan)}}
                  </Text>
                </div>
                <div class="tio-surface-sunken p-3">
                  <Text @size="xs" class="tio-fg-muted mb-0.5">Interest</Text>
                  <Text @size="sm" @weight="medium">
                    {{formatCurrency (interestBalance loan)}}
                  </Text>
                </div>
                <div class="tio-surface-sunken p-3">
                  <Text @size="xs" class="tio-fg-muted mb-0.5">Loan Type</Text>
                  <Text @size="sm" @weight="medium" class="truncate">
                    {{loanClassLabel loan}}
                  </Text>
                </div>
              </div>

              {{! Progress for individual loan }}
              {{#if loan.originalAmount}}
                <div class="mt-3">
                  <ProgressBar
                    @value={{loanProgressPercent loan}}
                    @color={{if (isEven index) "indigo" "blue"}}
                    @size="sm"
                  />
                  <Text @size="xs" class="tio-fg-disabled mt-1">
                    {{loanProgressPercent loan}}% paid off
                  </Text>
                </div>
              {{/if}}
            </div>
          {{else}}
            <div class="tio-bg-sunken rounded-xl p-8 text-center">
              <PhBank @size={{48}} class="mx-auto tio-fg-muted mb-3" />
              <Text class="tio-fg-muted">No active loans found for this account.</Text>
            </div>
          {{/each}}
        </div>
      </div>

      {{! Quick Actions }}
      <div class="mt-6">
        <SectionHeader
          @icon={{PhSparkle}}
          @title="Quick Actions"
          @iconClass="tio-fg-info"
          @iconSize={{24}}
        />
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <NavCard
            @layout="horizontal"
            @route="authenticated.slr.dashboard"
            @icon={{PhHandshake}}
            @title="Contribution History"
            @subtitle="View payment timeline"
            @intent="brand"
          />

          <NavCard
            @layout="horizontal"
            @route="authenticated.repaying-student-debt.repayment-strategy-finder"
            @icon={{PhTarget}}
            @title="Strategy Finder"
            @subtitle="Find best repayment path"
            @intent="success"
          />

          <NavCard
            @layout="horizontal"
            @route="authenticated.observability.upload"
            @query={{hash
              source="loan-detail"
              providers="nslds,statement"
              returnTo="authenticated.account-activity.accounts"
            }}
            @icon={{PhCloudArrowUp}}
            @title="Upload Documents"
            @subtitle="Update loan information"
            @intent="info"
          />
        </div>
      </div>
    </VStack>
  </template>
}

// --- Template helpers ---

function formatRate(rate: number): string {
  return (rate || 0).toFixed(2);
}

function isGreater(a: number, b: number): boolean {
  return a > b;
}

function sub(a: number, b: number): number {
  return a - b;
}

function isEven(index: number): boolean {
  return index % 2 === 0;
}

function lastFour(value: string): string {
  return (value || '').slice(-4);
}

function loanTypeName(loan: LoanModel): string {
  const loanType = loan.observation?.['loan-type'] || loan.observation?.['raw-loan-type'] || '';
  if (loanType) return loanType;
  const cls = loan.loanClass?.toLowerCase() || '';
  if (cls.includes('stafford')) return 'Stafford Loan';
  if (cls.includes('plus')) return 'PLUS Loan';
  if (cls.includes('private')) return 'Private Loan';
  if (cls.includes('consolidat')) return 'Consolidation Loan';
  return 'Student Loan';
}

function loanClassLabel(loan: LoanModel): string {
  return loan.loanClass || 'Unknown';
}

function loanInterestRate(loan: LoanModel): number {
  return loan.observation?.['interest-rate'] || 0;
}

function principalBalance(loan: LoanModel): number {
  return loan.observation?.['principal-balance'] || 0;
}

function interestBalance(loan: LoanModel): number {
  return loan.observation?.['interest-balance'] || 0;
}

function loanProgressPercent(loan: LoanModel): number {
  if (!loan.originalAmount) return 0;
  const paid = loan.originalAmount - (loan.currentBalance || 0);
  return Math.max(0, Math.min(100, Math.round((paid / loan.originalAmount) * 100)));
}

function accountType(loans: LoanModel[]): string {
  if (!loans.length) return 'Unknown';
  const cls = loans[0]?.loanClass?.toLowerCase() || '';
  if (cls.includes('private')) return 'Private';
  if (cls.includes('refinance')) return 'Refinanced';
  return 'Federal';
}
